import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Wallet, TrendingUp, Coins, Home, Settings as SettingsIcon,
  Sparkles, ChevronRight,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { usePreferences } from '../hooks/usePreferences'
import { supabase } from '../lib/supabase'
import { buildInviteUrl, buildInviteMessage } from '../lib/inviteLink'

type NavItem = {
  Icon:     LucideIcon
  title:    string
  subtitle: string
  to:       string
  tone:     'warn' | 'ok'
}

export default function More() {
  const navigate    = useNavigate()
  const { session } = useAuth()
  const { isPremium } = usePreferences()

  const [referralCode,   setReferralCode]   = useState<string | null>(null)
  const [referralCount,  setReferralCount]  = useState(0)
  const [freeMonths,     setFreeMonths]     = useState(0)
  const [shareStatus,    setShareStatus]    = useState<'idle' | 'copied'>('idle')
  const [referralLoaded, setReferralLoaded] = useState(false)

  const firstName = session?.user?.user_metadata?.display_name?.split(' ')[0] ?? null

  useEffect(() => { document.title = 'More — Pantry' }, [])

  useEffect(() => {
    if (!session?.user.id) return

    async function loadReferral() {
      const { data: userData } = await supabase
        .from('users')
        .select('referral_code, household_id')
        .eq('id', session!.user.id)
        .single()

      if (!userData) return

      let code = userData.referral_code
      if (!code) {
        code = Math.random().toString(36).substring(2, 8).toUpperCase()
        await supabase.from('users').update({ referral_code: code }).eq('id', session!.user.id)
      }
      setReferralCode(code)

      const { count } = await supabase
        .from('users')
        .select('id', { count: 'exact', head: true })
        .eq('referred_by', code)

      setReferralCount(count ?? 0)

      if (userData.household_id) {
        const { data: household } = await supabase
          .from('households')
          .select('free_months_earned')
          .eq('id', userData.household_id)
          .single()
        setFreeMonths(household?.free_months_earned ?? 0)
      }

      setReferralLoaded(true)
    }

    loadReferral()
  }, [session?.user.id])

  async function handleShareReferral() {
    if (!referralCode) return
    const url  = buildInviteUrl(referralCode)
    const text = buildInviteMessage(referralCode, firstName)

    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({ title: 'Pantry', text, url })
        return
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return
      }
    }

    try {
      await navigator.clipboard.writeText(url)
      setShareStatus('copied')
      setTimeout(() => setShareStatus('idle'), 2000)
    } catch {
      setShareStatus('idle')
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/')
  }

  const sections: { label: string; items: NavItem[] }[] = [
    {
      label: 'Insights',
      items: [
        { Icon: Wallet,     title: 'Savings',       subtitle: 'Waste, budget & spending trends',   to: '/savings',       tone: 'warn' },
        { Icon: TrendingUp, title: 'Price history', subtitle: 'Track how grocery prices change',   to: '/price-history', tone: 'warn' },
        { Icon: Coins,      title: 'Store prices',  subtitle: 'Compare prices across your stores', to: '/store-prices',  tone: 'warn' },
      ],
    },
    {
      label: 'Household',
      items: [
        { Icon: Home,          title: 'My household', subtitle: 'Members, shopping day & schedule', to: '/household', tone: 'ok' },
        { Icon: SettingsIcon,  title: 'Settings',     subtitle: 'Theme, notifications & more',      to: '/settings',  tone: 'ok' },
      ],
    },
  ]

  return (
    <div className="bg-cream min-h-[100dvh] pb-28">

      <header className="px-4 pt-8 pb-4">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
          More
        </div>
        <h1 className="font-serif text-[28px] font-normal leading-[1.05] tracking-[-0.015em] text-ink">
          More from <em className="italic">Pantry</em>
        </h1>
      </header>

      <div className="px-4 flex flex-col gap-5">

        {/* ── Referral card (forest gradient kept — it's the one brand moment) ── */}
        <section
          className="rounded-[18px] overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #2B4A3A 0%, #3E6A54 100%)' }}
        >
          <div className="px-5 pt-5 pb-5 text-forest-ink">
            <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-sand mb-1">
              Refer a friend
            </div>
            <h2 className="font-serif text-[22px] font-normal leading-[1.1] tracking-[-0.01em]">
              Give a month, get a <em className="italic text-sand">month</em>.
            </h2>
            <p className="font-serif text-[13px] italic text-forest-ink/75 mt-1 leading-[1.4]">
              Share Pantry+ with friends. You both get a free month when they subscribe.
            </p>
            <p className="font-serif italic text-ink-muted text-[11px] mt-1">
              Credit appears on your next bill automatically.
            </p>

            {referralLoaded && (
              <div className="flex gap-2 mt-4">
                <div className="flex-1 rounded-[12px] bg-white/10 px-3 py-2.5">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-serif text-[20px] font-medium leading-none">{referralCount}</span>
                    <span className="text-[11px] italic text-forest-ink/70">referred</span>
                  </div>
                </div>
                <div className="flex-1 rounded-[12px] bg-white/10 px-3 py-2.5">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-serif text-[20px] font-medium leading-none">{freeMonths}</span>
                    <span className="text-[11px] italic text-forest-ink/70">free months</span>
                  </div>
                </div>
              </div>
            )}

            {referralCode && (
              <div className="mt-3 rounded-[12px] bg-white/10 px-4 py-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-forest-ink/60 mb-0.5">
                    Your code
                  </div>
                  <div className="font-serif text-[15px] font-medium tracking-[0.12em]">{referralCode}</div>
                </div>
                <button
                  onClick={handleShareReferral}
                  className="rounded-[10px] bg-cream px-3 py-2 text-[12px] font-bold text-forest"
                  style={{ opacity: shareStatus === 'copied' ? 0.8 : 1 }}
                >
                  {shareStatus === 'copied' ? 'Copied ✓' : 'Share'}
                </button>
              </div>
            )}

            {!referralLoaded && (
              <div className="mt-3 rounded-[12px] bg-white/10 px-4 py-3 animate-pulse h-12" />
            )}
          </div>
        </section>

        {/* ── Upgrade card (free users only) — clean paper, not dark gradient ── */}
        {!isPremium && (
          <button
            onClick={() => navigate('/paywall')}
            className="w-full rounded-[18px] border border-border-sand bg-paper p-5 flex items-center gap-4 text-left"
          >
            <div className="h-10 w-10 rounded-[12px] bg-cream-warm flex items-center justify-center text-terracotta flex-shrink-0">
              <Sparkles size={20} strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-0.5">
                Pantry+
              </div>
              <div className="font-serif text-[16px] font-medium text-ink leading-tight">
                Upgrade to <em className="italic">Pantry+</em>
              </div>
              <p className="font-serif text-[12px] italic text-ink-muted mt-0.5 leading-[1.4]">
                7-day free trial · $4.99/mo or $39.99/yr
              </p>
            </div>
            <ChevronRight size={16} strokeWidth={1.6} className="text-ink-muted flex-shrink-0" />
          </button>
        )}

        {/* ── Nav sections ── */}
        {sections.map(section => (
          <div key={section.label}>
            <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2 px-1">
              {section.label}
            </p>
            <div className="rounded-[18px] border border-border-sand bg-paper overflow-hidden">
              {section.items.map(({ Icon, title, subtitle, to, tone }, idx) => {
                const isLast  = idx === section.items.length - 1
                const tintBg  = tone === 'warn' ? 'bg-cream-warm'    : 'bg-ok-bg'
                const tintFg  = tone === 'warn' ? 'text-terracotta' : 'text-forest'
                return (
                  <button
                    key={to}
                    onClick={() => navigate(to)}
                    className={`w-full flex items-center gap-3.5 px-4 py-3.5 text-left ${
                      !isLast ? 'border-b border-border-hairline' : ''
                    }`}
                  >
                    <div className={`h-9 w-9 rounded-[10px] flex items-center justify-center flex-shrink-0 ${tintBg} ${tintFg}`}>
                      <Icon size={18} strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-[15px] font-medium text-ink">{title}</p>
                      <p className="font-serif text-[12px] italic text-ink-muted mt-0.5 leading-[1.3]">
                        {subtitle}
                      </p>
                    </div>
                    <ChevronRight size={16} strokeWidth={1.6} className="text-ink-muted flex-shrink-0" />
                  </button>
                )
              })}
            </div>
          </div>
        ))}

        {/* ── Account ── */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2 px-1">
            Account
          </p>
          <div className="rounded-[18px] border border-border-sand bg-paper overflow-hidden">
            <div className="px-4 py-3.5 border-b border-border-hairline">
              <p className="text-[11px] italic font-serif text-ink-muted">Signed in as</p>
              <p className="font-serif text-[14px] font-medium text-ink mt-0.5 truncate">
                {session?.user.email ?? '—'}
              </p>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full px-4 py-3.5 text-left font-serif text-[14px] font-semibold text-critical"
            >
              Sign out
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}
